import { QueryStatus, useQuery } from "@tanstack/react-query";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import { TrendUp } from "@phosphor-icons/react";

import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { type ChartConfig, ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedContent } from "@/components/layout/locked-content";
import { VolumeTrend } from "@/components/ai-consumer-research/overview/volume";

import { message } from "@/lib/utils";
import { ConsumerResearchApiFactory } from "@/apis/brand-os/consumer-research";

const sentimentTrendChartConfig = {
  positive: { label: "Positive", color: "var(--success)" },
  negative: { label: "Negative", color: "var(--destructive)" },
} satisfies ChartConfig;

type SentimentTrendRow = { month: string; positive: number; negative: number };

function SentimentTrendComponent({ status, chartRows }: { status: QueryStatus; chartRows: SentimentTrendRow[] }) {
  switch (status) {
    case "pending":
      return <SentimentTrendSkeleton />;
    case "error":
      return <LockedContent message={message} className="min-h-80" />;
    case "success":
      return chartRows.length ? <SentimentTrendChart data={chartRows} /> : <LockedContent message={message} className="min-h-80" />;
  }
}

export function SentimentTrendOverview() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <SentimentTrend />
      <VolumeTrend />
    </div>
  );
}

function SentimentTrendChart({ data }: { data: SentimentTrendRow[] }) {
  return (
    <ChartContainer config={sentimentTrendChartConfig} className="w-full max-h-96">
      <LineChart accessibilityLayer data={data} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
        <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-border/50" />
        <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} />
        <YAxis domain={[0, 100]} tickLine={false} axisLine={false} tickMargin={8} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} ticks={[0, 25, 50, 75, 100]} />
        <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="line" />} />
        <Line dataKey="positive" type="monotone" stroke="var(--color-positive)" strokeWidth={2} dot={{ r: 4, fill: "var(--color-positive)", strokeWidth: 0 }} />
        <Line dataKey="negative" type="monotone" stroke="var(--color-negative)" strokeWidth={2} dot={{ r: 4, fill: "var(--color-negative)", strokeWidth: 0 }} />
        <ChartLegend verticalAlign="bottom" content={<ChartLegendContent />} className="flex justify-start pt-6" />
      </LineChart>
    </ChartContainer>
  );
}

function SentimentTrend() {
  const { data: sentimentTrendData, status } = useQuery(ConsumerResearchApiFactory.Queries.OverviewSentimentTrend());

  const chartRows = (sentimentTrendData?.trend ?? []).map((item) => ({
    month: item.month,
    positive: item.positive_count,
    negative: item.negative_count,
  }));

  return (
    <Card className="lg:col-span-2 last:lg:col-span-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span className="flex size-9 items-center justify-center rounded-lg bg-success/10 text-success ">
            <TrendUp className="size-5" weight="bold" />
          </span>
          Sentiment Trend
        </CardTitle>
        <CardDescription className="pl-11">Across all channels</CardDescription>
        <CardAction>
          <span className="text-xs text-muted-foreground">Last 8 months</span>
        </CardAction>
      </CardHeader>
      <CardContent>
        <SentimentTrendComponent status={status} chartRows={chartRows} />
      </CardContent>
    </Card>
  );
}

function SentimentTrendSkeleton() {
  return (
    <div className="w-full h-80 flex flex-col justify-between">
      <div className="space-y-3">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-3 w-full rounded" />
        ))}
      </div>
      <div className="relative mt-6 h-full">
        <Skeleton className="absolute bottom-10 left-0 w-full h-1 rounded-full" />
        <Skeleton className="absolute bottom-20 left-0 w-full h-1 rounded-full opacity-60" />
      </div>
    </div>
  );
}
